import { useMemo, useState } from "react";
import { Box, Button, Card, TextField, Typography } from "@mui/material";
import { Check, Pencil, Trash2, X } from "lucide-react";
import {
  AddUserToUnitDialog,
  AddUserToUnitFormValues,
  UnitLinkedUsersDialog,
} from "../components/ResultDialogs";
import { WizardUnit, WizardUser } from "../types";
import { passtrackColors } from "../theme";

interface UnitsStepProps {
  units: WizardUnit[];
  users: WizardUser[];
  newUnitName: string;
  onNewUnitNameChange: (value: string) => void;
  onAddUnit: () => void;
  onRenameUnit: (id: string, name: string) => boolean;
  onRemoveUnit: (id: string) => void;
  onAddUserToUnit: (unitId: string, data: AddUserToUnitFormValues) => boolean;
}

const GRID_COLUMNS = "minmax(140px,1.8fr) minmax(110px,1.2fr) minmax(90px,1fr) 40px";

export default function UnitsStep({
  units,
  users,
  newUnitName,
  onNewUnitNameChange,
  onAddUnit,
  onRenameUnit,
  onRemoveUnit,
  onAddUserToUnit,
}: UnitsStepProps) {
  const [viewUsersUnitId, setViewUsersUnitId] = useState<string | null>(null);
  const [addUserUnitId, setAddUserUnitId] = useState<string | null>(null);
  const [editingUnitId, setEditingUnitId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState("");
  const [renameError, setRenameError] = useState<string | null>(null);

  const usersByUnitName = useMemo(() => {
    const map = new Map<string, WizardUser[]>();
    for (const user of users) {
      const unitName = user.unit.trim();
      if (!unitName) continue;
      const list = map.get(unitName) ?? [];
      list.push(user);
      map.set(unitName, list);
    }
    return map;
  }, [users]);

  const viewUsersUnit =
    units.find((unit) => unit.id === viewUsersUnitId) ?? null;
  const viewUsers = viewUsersUnit
    ? usersByUnitName.get(viewUsersUnit.name) ?? []
    : [];
  const addUserUnit = units.find((unit) => unit.id === addUserUnitId) ?? null;

  const startEditing = (unit: WizardUnit) => {
    if (unit.locked) return;
    setEditingUnitId(unit.id);
    setEditingName(unit.name);
    setRenameError(null);
  };

  const cancelEditing = () => {
    setEditingUnitId(null);
    setEditingName("");
    setRenameError(null);
  };

  const saveEditing = () => {
    if (!editingUnitId) return;
    const trimmed = editingName.trim();
    if (!trimmed) {
      setRenameError("El nombre no puede estar vacío.");
      return;
    }
    const saved = onRenameUnit(editingUnitId, trimmed);
    if (!saved) {
      setRenameError("Ese nombre ya está en uso.");
      return;
    }
    cancelEditing();
  };

  return (
    <>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "minmax(0,1fr) 340px" },
          gap: 3,
          alignItems: "start",
        }}
      >
        <Card variant="outlined" sx={{ p: 3.5, borderRadius: 2.5 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-end",
              justifyContent: "space-between",
              mb: 2.5,
              gap: 2,
            }}
          >
            <Box>
              <Typography fontWeight={600} color={passtrackColors.ink}>
                Unidades
              </Typography>
              <Typography sx={{ fontSize: 13, color: passtrackColors.textMuted }}>
                Espacios independientes que conforman la propiedad, como casas,
                departamentos, áreas u oficinas.
              </Typography>
            </Box>
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 600,
                color: passtrackColors.blue900,
                background: passtrackColors.cloud,
                borderRadius: 99,
                px: 1.25,
                py: 0.5,
                whiteSpace: "nowrap",
              }}
            >
              {units.length} unidades
            </Typography>
          </Box>

          <Box
            sx={{
              border: `1px solid ${passtrackColors.borderSubtle}`,
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: GRID_COLUMNS,
                gap: 1.5,
                px: 2,
                py: 1.5,
                background: passtrackColors.mist,
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                color: passtrackColors.textMuted,
              }}
            >
              <span>Unidad</span>
              <span>Usuarios</span>
              <span>Estado</span>
              <span />
            </Box>
            {units.map((u, i) => {
              const linkedUsers = usersByUnitName.get(u.name) ?? [];
              const count = linkedUsers.length;
              const isEditing = editingUnitId === u.id;
              return (
                <Box
                  key={u.id}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: GRID_COLUMNS,
                    gap: 1.5,
                    alignItems: "center",
                    px: 2,
                    py: 1.75,
                    borderTop: i
                      ? `1px solid ${passtrackColors.borderSubtle}`
                      : "none",
                    background: u.locked ? passtrackColors.mist : "#fff",
                  }}
                >
                  {isEditing ? (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                        <TextField
                          size="small"
                          value={editingName}
                          onChange={(e) => {
                            setEditingName(e.target.value);
                            if (renameError) setRenameError(null);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              saveEditing();
                            }
                            if (e.key === "Escape") {
                              e.preventDefault();
                              cancelEditing();
                            }
                          }}
                          autoFocus
                          fullWidth
                          error={Boolean(renameError)}
                          inputProps={{ "aria-label": "Nombre de la unidad" }}
                        />
                        <Box
                          component="button"
                          type="button"
                          aria-label="Guardar nombre"
                          onClick={saveEditing}
                          sx={{
                            display: "inline-flex",
                            p: 0.5,
                            background: "none",
                            border: "none",
                            color: passtrackColors.green700,
                            cursor: "pointer",
                          }}
                        >
                          <Check size={16} />
                        </Box>
                        <Box
                          component="button"
                          type="button"
                          aria-label="Cancelar edición"
                          onClick={cancelEditing}
                          sx={{
                            display: "inline-flex",
                            p: 0.5,
                            background: "none",
                            border: "none",
                            color: passtrackColors.textMuted,
                            cursor: "pointer",
                          }}
                        >
                          <X size={16} />
                        </Box>
                      </Box>
                      {renameError ? (
                        <Typography sx={{ fontSize: 11, color: passtrackColors.red }}>
                          {renameError}
                        </Typography>
                      ) : null}
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.75,
                        minWidth: 0,
                      }}
                    >
                      <Typography
                        noWrap
                        sx={{
                          fontWeight: 600,
                          fontSize: 14,
                          color: passtrackColors.ink,
                        }}
                      >
                        {u.name}
                      </Typography>
                      {!u.locked && (
                        <Box
                          component="button"
                          type="button"
                          aria-label={`Editar ${u.name}`}
                          onClick={() => startEditing(u)}
                          sx={{
                            display: "inline-flex",
                            p: 0.4,
                            background: "none",
                            border: "none",
                            color: passtrackColors.textMuted,
                            cursor: "pointer",
                            flexShrink: 0,
                            borderRadius: 1,
                            "&:hover": {
                              color: passtrackColors.blue900,
                              background: passtrackColors.cloud,
                            },
                          }}
                        >
                          <Pencil size={14} />
                        </Box>
                      )}
                    </Box>
                  )}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      gap: 0.35,
                    }}
                  >
                    <Box
                      component={count > 0 ? "button" : "span"}
                      type={count > 0 ? "button" : undefined}
                      onClick={
                        count > 0 ? () => setViewUsersUnitId(u.id) : undefined
                      }
                      sx={{
                        background: "none",
                        border: "none",
                        p: 0,
                        font: "inherit",
                        fontSize: 13,
                        fontWeight: 600,
                        color:
                          count > 0
                            ? passtrackColors.blue900
                            : passtrackColors.textMuted,
                        cursor: count > 0 ? "pointer" : "default",
                        textDecoration: count > 0 ? "underline" : "none",
                        textUnderlineOffset: 2,
                        "&:hover":
                          count > 0
                            ? { color: passtrackColors.blue }
                            : undefined,
                      }}
                    >
                      Usuarios: {count}
                    </Box>
                    <Box
                      component="button"
                      type="button"
                      onClick={() => setAddUserUnitId(u.id)}
                      sx={{
                        background: "none",
                        border: "none",
                        p: 0,
                        font: "inherit",
                        fontSize: 12,
                        fontWeight: 600,
                        color: passtrackColors.green700,
                        cursor: "pointer",
                        "&:hover": { textDecoration: "underline" },
                      }}
                    >
                      Agregar usuario
                    </Box>
                  </Box>
                  <Typography
                    sx={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: u.locked
                        ? passtrackColors.blue900
                        : passtrackColors.green700,
                      background: u.locked
                        ? passtrackColors.cloud
                        : passtrackColors.greenMist,
                      borderRadius: 99,
                      px: 1.25,
                      py: 0.4,
                      justifySelf: "start",
                    }}
                  >
                    {u.locked ? "Requerida" : "Activa"}
                  </Typography>
                  <Box
                    component="button"
                    type="button"
                    aria-label={`Eliminar ${u.name}`}
                    onClick={() => !u.locked && onRemoveUnit(u.id)}
                    disabled={u.locked}
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      p: 0.5,
                      background: "none",
                      border: "none",
                      color: u.locked
                        ? passtrackColors.borderDefault
                        : passtrackColors.red,
                      cursor: u.locked ? "not-allowed" : "pointer",
                      justifySelf: "center",
                      borderRadius: 1,
                      "&:hover": u.locked
                        ? undefined
                        : { background: "rgba(228, 80, 31, 0.08)" },
                    }}
                  >
                    <Trash2 size={14} />
                  </Box>
                </Box>
              );
            })}
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "minmax(0,2fr) auto" },
              gap: 1.25,
              alignItems: "end",
              background: passtrackColors.mist,
              border: `1px dashed ${passtrackColors.borderDefault}`,
              borderRadius: 2,
              p: 2,
              mt: 2,
            }}
          >
            <TextField
              label="Nombre de la unidad"
              size="small"
              fullWidth
              value={newUnitName}
              onChange={(e) => onNewUnitNameChange(e.target.value)}
              placeholder="Depto. 103"
            />
            <Button variant="contained" color="secondary" onClick={onAddUnit}>
              Agregar unidad
            </Button>
          </Box>
        </Card>

        <Card variant="outlined" sx={{ p: 3, borderRadius: 2.5 }}>
          <Typography
            fontWeight={600}
            sx={{ mb: 1.5 }}
            color={passtrackColors.ink}
          >
            Cómo funcionan las unidades
          </Typography>
          <Typography
            sx={{
              fontSize: 13,
              lineHeight: 1.55,
              color: passtrackColors.graphite,
              mb: 1.5,
            }}
          >
            Los usuarios y sus visitantes siempre quedan asociados a una
            unidad, y el historial de accesos se agrupa por ella.
          </Typography>
          <Box
            sx={{
              display: "flex",
              gap: 1.25,
              background: "#fff",
              border: `1px solid ${passtrackColors.borderSubtle}`,
              borderRadius: 1.5,
              p: 1.75,
            }}
          >
            <Typography
              sx={{ fontSize: 12, lineHeight: 1.5, color: passtrackColors.graphite }}
            >
              La unidad{" "}
              <strong style={{ color: passtrackColors.ink }}>Administración</strong>{" "}
              se crea siempre y no puede eliminarse: es la que gobierna la
              propiedad.
            </Typography>
          </Box>
        </Card>
      </Box>

      <UnitLinkedUsersDialog
        open={Boolean(viewUsersUnit)}
        unitName={viewUsersUnit?.name ?? ""}
        users={viewUsers.map((user) => ({
          id: user.id,
          name: user.name,
          email: user.email,
          role: user.role,
        }))}
        onClose={() => setViewUsersUnitId(null)}
      />

      <AddUserToUnitDialog
        open={Boolean(addUserUnit)}
        unitName={addUserUnit?.name ?? ""}
        roleLabel={
          addUserUnit?.locked ? "Administrador" : "Usuario Principal"
        }
        onCancel={() => setAddUserUnitId(null)}
        onSave={(values) =>
          addUserUnit ? onAddUserToUnit(addUserUnit.id, values) : false
        }
      />
    </>
  );
}
